/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG, FCORG_BLACKLIST, GMTVP} from '../../../../common/constants';
import {StateService} from '../../../../core/state/state.service';
import {PropulsionPortfolioReportRequest} from '../../../../models/reports/propulsion-portfolio-report-request.model';
import {SelectableOptions} from '../../../../models/selectable-options';
import {ExcelExportService} from '../../../../services/excel-export.service';
import {FcorgService} from '../../../../services/fcorg.service';
import {PropulsionPortfolioLoadService} from '../../../../services/propulsion-portfolio-load.service';

@Component({
    selector: 'app-propulsion-portfolio-report',
    templateUrl: './propulsion-portfolio-report.component.html',
    styleUrl: './propulsion-portfolio-report.component.scss',
    standalone: false
})
export class PropulsionPortfolioReportComponent implements OnInit {

  REGION = 'REGION';
  SUBREGION = 'SUBREGION';
  PLNGMKT = 'PLNGMKT';
  ARCH = 'ARCH';
  PRGMCODE = 'PRGMCODE';
  BODYSTYLE = 'BODYSTYLE';
  ENGFAM = 'ENGFAM';
  TRANFAM = 'TRANFAM';


  showSpinner: boolean;

  filteredData = [];
  allFilterOptions = [];


  fcorgs = new SelectableOptions();
  regions = new SelectableOptions();
  subregions = new SelectableOptions();
  planningMarkets = new SelectableOptions();
  architectures = new SelectableOptions();
  programCodes = new SelectableOptions();
  bodyStyles = new SelectableOptions();
  engineFams = new SelectableOptions();
  transmissionFams = new SelectableOptions();

  portTypes = [
    {
      id: 0,
      label: "Business Plan"
    },
    {
      id: 1,
      label: "TVP"
    }
  ];
  selectedPortType: { id: number, label: string };
  dateAudit: boolean;

  constructor(readonly propulsionPortfolioService: PropulsionPortfolioLoadService,
              readonly stateService: StateService,
              readonly fcorgService: FcorgService,
              readonly excelService: ExcelExportService,
              public dialog: MatDialog,
              public snackBar: FabMessageService) {
  }

  ngOnInit() {
    this.showSpinner = false;
    this.selectedPortType = this.portTypes[0];
    this.dateAudit = false;
    this.getFcorgs();
  }

  /**
   * Gets the fcorgs the user has access to
   */
  getFcorgs() {
    this.showSpinner = true;
    this.fcorgService.getFcorgsById(this.stateService.getUserGmid()).subscribe({
        next: (data) => {
          const customFcorgBlacklist = FCORG_BLACKLIST.concat(["GME", GMTVP])
          this.fcorgs.options = data.filter((element: string) => !customFcorgBlacklist.includes(element));
        },
        error: (error) => {
          this.snackBar.displayError(error, DISMISS_MSG);
          this.showSpinner = false;
        },
        complete: () => {
          this.showSpinner = false;
        }
      }
    )
  }

  /**
   * retrieves all the filter data for the selected fcorgs
   */
  getFilters() {
    if (this.fcorgs.selectedOptions.length !== 0) {
      this.resetAllFilterOptions();
      this.resetAllSelectedFilter();
      this.showSpinner = true;
      this.propulsionPortfolioService.getPortfolioReportFilters(this.selectedPortType.label, this.fcorgs.selectedOptions).subscribe({
        next: (data: any[]) => {
          data.map(element => element.ARCH = element.ARCH === null ? '_NoMatchToVeh' : element.ARCH);

          this.allFilterOptions = data;
          this.filteredData = data;
        },
        error: (error) => {
          this.snackBar.displayError(error, DISMISS_MSG);
          this.showSpinner = false;
        },
        complete: () => {
          this.showSpinner = false;
          this.initializeFilterOptions();
        }
      });
    }
  }

  /**
   * Event handler for change in portfolio type
   */
  onPortTypeChange() {
    this.fcorgs.selectedOptions = [];
    this.resetAllFilterOptions();
    this.resetAllSelectedFilter();
    if (this.selectedPortType.id === 1) {
      this.fcorgs.options = [GMTVP];
    } else {
      this.getFcorgs();
    }
  }

  /**
   * gets the portfolio report
   */
  getPortfolioReport() {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const fileName = 'PropPort_' + currDate;

    const dto: PropulsionPortfolioReportRequest = {
      portType: this.selectedPortType.label,
      gmid: this.stateService.getUserGmid(),
      fcorgs: this.fcorgs.selectedOptions,
      dateAudit: this.dateAudit ? 1 : 0,
      regions: this.regions.selectedOptions,
      subregions: this.subregions.selectedOptions,
      planningMarkets: this.planningMarkets.selectedOptions,
      architectures: this.architectures.selectedOptions,
      programCodes: this.programCodes.selectedOptions,
      bodyStyles: this.bodyStyles.selectedOptions,
      engineFams: this.engineFams.selectedOptions,
      transFams: this.transmissionFams.selectedOptions
    }

    this.showSpinner = true;
    this.propulsionPortfolioService.getPortfolioReport(dto).subscribe(
      {
        next: (data) => {
          this.excelService.exportToExcelFromServer(data, fileName);
        },
        error: (error) => {
          this.showSpinner = false;
          this.snackBar.displayError(error, DISMISS_MSG);
        },
        complete: () => {
          this.showSpinner = false;
          this.snackBar.displaySuccess('Successfully retrieved data. Exporting...', DISMISS_MSG);

        }
      }
    );
  }

  //Filter stuff down here
  /**
   * Function used to update the filtered data based off of the selected filters for a particular filter type
   * @param filterType the filter type (which filter it is)
   * @param selectedFilters list of filter values that were selected from the filter
   */
  applyFilter(filterType: string, selectedFilters: any): void {
    if (selectedFilters.length > 0) {
      this.filteredData = this.filteredData.filter(detailRecord => selectedFilters.includes(detailRecord[filterType].toUpperCase()));
    }
  }

  /**
   * Event handler whenever a filter changes
   * @param filterType the filter type (which filter it is)
   */
  onFilterChange(filterType: string): void {
    // Reset the filtered data to the original data
    this.filteredData = this.allFilterOptions;
    // Reset the selected filters for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
    // Update the filtered data based off of which filters are selected
    this.applyFilter(this.REGION, this.regions.selectedOptions);
    this.applyFilter(this.SUBREGION, this.subregions.selectedOptions);
    this.applyFilter(this.PLNGMKT, this.planningMarkets.selectedOptions);
    this.applyFilter(this.ARCH, this.architectures.selectedOptions);
    this.applyFilter(this.PRGMCODE, this.programCodes.selectedOptions);
    this.applyFilter(this.BODYSTYLE, this.bodyStyles.selectedOptions);
    this.applyFilter(this.ENGFAM, this.engineFams.selectedOptions);
    this.applyFilter(this.TRANFAM, this.transmissionFams.selectedOptions);

    // Update the filter options for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
  }

  /**
   * Initialize the filter options for all the filters
   */
  initializeFilterOptions(): void {
    this.regions.options = this.setFilterOption(this.REGION)
    this.setCascadingFilterOptions(this.REGION);
  }

  /**
   * Helper function to return back a list of available filter options for the specified filter
   * @param filterFieldName the name of the filter to set the filter options for
   */
  setFilterOption(filterFieldName: string): string[] {
    return [...new Set(this.filteredData
    .sort((a, b) => a[filterFieldName].toUpperCase() > b[filterFieldName].toUpperCase() ? 1 : -1)
    .map(detailRecord => detailRecord[filterFieldName].toUpperCase()))];
  }

  /**
   * Function used to set the filter options for the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  setCascadingFilterOptions(filterType: string): void {
    switch (filterType) {
      case this.REGION:
        this.subregions.options = this.setFilterOption(this.SUBREGION);
        this.subregions.selectedOptions = [];
      // eslint-disable-next-line
      case this.SUBREGION:
        this.planningMarkets.options = this.setFilterOption(this.PLNGMKT);
        this.planningMarkets.selectedOptions = [];
      // eslint-disable-next-line
      case this.PLNGMKT:
        this.architectures.options = this.setFilterOption(this.ARCH);
        this.architectures.selectedOptions = [];
      // eslint-disable-next-line
      case this.ARCH:
        this.programCodes.options = this.setFilterOption(this.PRGMCODE);
        this.programCodes.selectedOptions = [];
      // eslint-disable-next-line
      case this.PRGMCODE:
        this.bodyStyles.options = this.setFilterOption(this.BODYSTYLE);
        this.bodyStyles.selectedOptions = [];
      // eslint-disable-next-line
      case this.BODYSTYLE:
        this.engineFams.options = this.setFilterOption(this.ENGFAM);
        this.engineFams.selectedOptions = [];
      // eslint-disable-next-line
      case this.ENGFAM:
        this.transmissionFams.options = this.setFilterOption(this.TRANFAM);
        this.transmissionFams.selectedOptions = [];
    }
  }

  /**
   * Resets the output filters for the pivot table data
   */
  resetAllFilterOptions(): void {
    // Clear the filter options
    this.regions.options = [];
    this.subregions.options = [];
    this.planningMarkets.options = [];
    this.architectures.options = [];
    this.programCodes.options = [];
    this.bodyStyles.options = [];
    this.engineFams.options = [];
    this.transmissionFams.options = [];
  }

  /**
   * Resets all the selected filters from the filter dropdowns
   */
  resetAllSelectedFilter(): void {
    // Clear the selected filters
    this.regions.selectedOptions = [];
    this.subregions.selectedOptions = [];
    this.planningMarkets.selectedOptions = [];
    this.architectures.selectedOptions = [];
    this.programCodes.selectedOptions = [];
    this.bodyStyles.selectedOptions = [];
    this.engineFams.selectedOptions = [];
    this.transmissionFams.selectedOptions = [];
  }

}
